import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaFacebook,
  FaLinkedin,
  FaYoutube,
  FaInstagram,
  FaArrowUp,
  FaHeart,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { FaXTwitter, FaTiktok } from 'react-icons/fa6';
import { FaWhatsapp } from 'react-icons/fa';
import { socialLinks, contactInfo } from '../../data/siteData';
import './Footer.css';

const Footer = () => {
  const footerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);
  const currentYear = new Date().getFullYear();
  const footerEmail = contactInfo.footerEmail || contactInfo.email;

  const courses = [
    { name: 'Noorani Qaida Course', href: '/courses/noorani-qaida-course' },
    { name: 'Quran Memorization Course', href: '/courses/quran-memorization-course' },
    { name: 'Tajweed Course', href: '/courses/tajweed-course' },
    { name: 'Islamic Studies Course', href: '/courses/islamic-studies-course' },
    { name: 'Arabic Language Course', href: '/courses/arabic-language-course' },
    { name: 'Namaz Course', href: '/courses/namaz-course' },
    { name: 'Hadith Course', href: '/courses/hadith-course' }
  ];

  const company = [
    { name: 'About Us', href: '/about-us' },
    { name: 'Our Teachers', href: '/our-teachers' },
    { name: 'Become A Teacher', href: '/become-a-teacher' },
    { name: 'Free Trial', href: '/free-trial' },
    { name: 'Contact Us', href: '/contact-us' }
  ];

  const resources = [
    { name: 'All Courses', href: '/courses' },
    { name: 'Fee Plans', href: '/study-plans-fees' },
    { name: 'Payment Options', href: '/payment-options' }
  ];

  const socialLinksList = [
    { name: 'LinkedIn', href: socialLinks.linkedin, icon: FaLinkedin, color: 'hover:text-blue-600' },
    { name: 'Twitter', href: socialLinks.twitter, icon: FaXTwitter, color: 'hover:text-sky-500' },
    { name: 'Facebook', href: socialLinks.facebook, icon: FaFacebook, color: 'hover:text-blue-700' },
    { name: 'Instagram', href: socialLinks.instagram, icon: FaInstagram, color: 'hover:text-pink-600' },
    { name: 'YouTube', href: socialLinks.youtube, icon: FaYoutube, color: 'hover:text-red-500' },
    { name: 'TikTok', href: socialLinks.tiktok, icon: FaTiktok, color: 'hover:text-gray-900' }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer ref={footerRef} className={`footer-aiksol-style ${isVisible ? 'footer-visible' : ''}`}>
      {/* Main Footer Content */}
      <div className={`footer-main ${isVisible ? 'footer-animate-in' : ''}`}>
        <div className="footer-container">
          <div className="footer-grid-main">
            {/* Company Information */}
            <div className={`footer-col-brand footer-animate-item`} style={{ animationDelay: '0.15s' }}>
              <div className="footer-logo-wrap">
                <Link to="/" className="footer-logo-link" aria-label="Al-Noor Tutors Academy — Home">
                  <img src="/logo.png" alt="Al-Noor Tutors Academy" className="footer-logo-img" width="400" height="88" />
                </Link>
              </div>
              <p className="footer-desc">
                Leading ALNoor Tutors providing comprehensive Quranic education to students worldwide.
                Learn the Holy Quran with expert tutors from the comfort of your home.
              </p>

              <div className="footer-contact-list">
                <div className="footer-contact-item">
                  <FaMapMarkerAlt className="footer-contact-icon" />
                  <span className="footer-contact-text">{contactInfo.location}</span>
                </div>
                <div className="footer-contact-item">
                  <FaPhone className="footer-contact-icon" />
                  <a href={`tel:${contactInfo.phone1.replace(/[^0-9+]/g, '')}`} className="footer-contact-link">
                    {contactInfo.phone1}
                  </a>
                </div>
                <div className="footer-contact-item">
                  <FaWhatsapp className="footer-contact-icon" />
                  <a
                    href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-contact-link"
                  >
                    {contactInfo.whatsapp}
                  </a>
                </div>
                <div className="footer-contact-item">
                  <FaEnvelope className="footer-contact-icon" />
                  <a href={`mailto:${footerEmail}`} className="footer-contact-link">
                    {footerEmail}
                  </a>
                </div>
                <div className="footer-contact-item">
                  <FaClock className="footer-contact-icon" />
                  <span className="footer-contact-text">24/7 Flexible Schedule</span>
                </div>
              </div>
            </div>

            {/* Our Courses */}
            <div className="footer-col footer-animate-item" style={{ animationDelay: '0.25s' }}>
              <h4 className="footer-col-title">Our Courses</h4>
              <ul className="footer-link-list">
                {courses.map((item, i) => (
                  <li key={item.href} className="footer-link-stagger" style={{ animationDelay: `${0.3 + i * 0.05}s` }}>
                    <Link to={item.href} className="footer-link">
                      <span className="footer-link-inner">{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div className="footer-col footer-animate-item" style={{ animationDelay: '0.35s' }}>
              <h4 className="footer-col-title">Company</h4>
              <ul className="footer-link-list">
                {company.map((item, i) => (
                  <li key={item.href} className="footer-link-stagger" style={{ animationDelay: `${0.4 + i * 0.05}s` }}>
                    <Link to={item.href} className="footer-link">
                      <span className="footer-link-inner">{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources & Social */}
            <div className="footer-col footer-animate-item" style={{ animationDelay: '0.45s' }}>
              <h4 className="footer-col-title">Resources</h4>
              <ul className="footer-link-list">
                {resources.map((item, i) => (
                  <li key={item.href} className="footer-link-stagger" style={{ animationDelay: `${0.5 + i * 0.05}s` }}>
                    <Link to={item.href} className="footer-link">
                      <span className="footer-link-inner">{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Social Media */}
              <div className="footer-social-section">
                <h5 className="footer-social-title">Follow Us</h5>
                <div className="footer-social-wrap">
                  {socialLinksList.map((social, i) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`footer-social-icon ${social.color}`}
                      title={social.name}
                      style={{ animationDelay: `${0.7 + i * 0.08}s` }}
                    >
                      <social.icon />
                    </a>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="footer-cta-wrap">
                <Link to="/free-trial" className="footer-cta-btn">
                  Start Free Trial
                  <FaExternalLinkAlt className="footer-cta-icon" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer-bottom-bar">
        <div className="footer-container">
          <div className="footer-bottom-inner">
            <div className="footer-bottom-left">
              <span className="footer-copyright">
                &copy; {currentYear} ALNoor Tutors. All rights reserved.
              </span>
            </div>

            <div className="footer-bottom-right">
              <div className="footer-made-with">
                <span>Made with</span>
                <FaHeart className="footer-heart footer-heart-beat" />
                <span>for Muslim Ummah</span>
              </div>

              <button
                onClick={scrollToTop}
                className="footer-scroll-btn"
                title="Back to top"
                aria-label="Scroll to top"
              >
                <FaArrowUp />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
